<template>
  <div class="proyectos-editar">
    <!-- Header -->
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">Editar Proyecto</h1>
        <p class="content-header-subtitle">Modificar información del proyecto</p>
      </div>
      <div class="content-header-right">
        <b-button variant="outline-secondary" @click="volver">
          <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
          Volver
        </b-button>
      </div>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <b-spinner class="text-primary"></b-spinner>
      <p class="mt-2">Cargando proyecto...</p>
    </div>

    <!-- Formulario -->
    <b-card v-else>
      <b-form @submit.prevent="actualizarProyecto">
        <b-row>
          <b-col md="6">
            <b-form-group label="Nombre del Proyecto" label-for="nombre" required>
              <b-form-input
                id="nombre"
                v-model="proyecto.nombre"
                type="text"
                placeholder="Ingrese el nombre del proyecto"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Responsable" label-for="responsable" required>
              <b-form-select
                id="responsable"
                v-model="proyecto.responsable"
                :options="opcionesResponsables"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Fecha de Inicio" label-for="fechaInicio" required>
              <b-form-datepicker
                id="fechaInicio"
                v-model="proyecto.fechaInicio"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Fecha de Fin Estimada" label-for="fechaFin">
              <b-form-datepicker
                id="fechaFin"
                v-model="proyecto.fechaFin"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Presupuesto" label-for="presupuesto">
              <b-input-group>
                <b-input-group-prepend is-text>€</b-input-group-prepend>
                <b-form-input
                  id="presupuesto"
                  v-model="proyecto.presupuesto"
                  type="number"
                  placeholder="0.00"
                />
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Estado" label-for="estado">
              <b-form-select
                id="estado"
                v-model="proyecto.estado"
                :options="opcionesEstado"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Descripción" label-for="descripcion">
          <b-form-textarea
            id="descripcion"
            v-model="proyecto.descripcion"
            rows="4"
            placeholder="Descripción detallada del proyecto..."
          />
        </b-form-group>


        <!-- Sección de Obras -->
        <div class="mt-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h4 class="mb-0">
              <feather-icon icon="BuildingIcon" size="20" class="mr-2" />
              Obras del Proyecto
              <b-badge variant="primary" class="ml-2">{{ proyecto.obras ? proyecto.obras.length : 0 }}</b-badge>
            </h4>
            <b-button variant="primary" size="sm" @click="mostrarModalObra">
              <feather-icon icon="PlusIcon" size="16" class="mr-50" />
              Añadir Obra
            </b-button>
          </div>
          
          <div v-if="proyecto.obras && proyecto.obras.length > 0" class="row">
            <div v-for="(obra, index) in proyecto.obras" :key="obra.idObra" class="col-md-6 col-lg-4 mb-3">
              <b-card class="h-100 obra-card" no-body>
                <b-card-body class="p-3">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <h6 class="card-title mb-0 text-truncate" :title="obra.direccion">
                      {{ obra.direccion }}
                    </h6>
                    <b-dropdown variant="link" toggle-class="text-decoration-none p-0" no-caret right>
                      <template #button-content>
                        <feather-icon icon="MoreVerticalIcon" size="16" />
                      </template>
                      <b-dropdown-item @click="editarObra(obra, index)">
                        <feather-icon icon="EditIcon" size="14" class="mr-2" />
                        Editar
                      </b-dropdown-item>
                      <b-dropdown-item @click="eliminarObra(index)" class="text-danger">
                        <feather-icon icon="TrashIcon" size="14" class="mr-2" />
                        Eliminar
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                  
                  <div class="mb-2">
                    <small class="text-muted d-flex align-items-center">
                      <feather-icon icon="MapPinIcon" size="14" class="mr-1" />
                      {{ obra.municipio }}, {{ obra.provincia }}
                    </small>
                  </div>
                  
                  <div class="mb-2">
                    <b-badge 
                      :variant="getEstadoObraVariant(obra.nombreEstadoObra)" 
                      class="mr-1"
                    >
                      {{ obra.nombreEstadoObra }}
                    </b-badge>
                    <b-badge variant="outline-secondary">
                      {{ obra.nombreFase }}
                    </b-badge>
                  </div>
                  
                  <div v-if="obra.empresa" class="mb-2">
                    <small class="text-muted d-flex align-items-center">
                      <feather-icon icon="Building2Icon" size="14" class="mr-1" />
                      {{ obra.empresa.nombre || obra.nombreEmpresa }}
                    </small>
                  </div>
                  
                  <div v-if="obra.fechaInicioObra" class="mb-2">
                    <small class="text-muted d-flex align-items-center">
                      <feather-icon icon="CalendarIcon" size="14" class="mr-1" />
                      Inicio: {{ formatearFecha(obra.fechaInicioObra) }}
                    </small>
                  </div>
                  
                  <div v-if="obra.fechaFinObra" class="mb-2">
                    <small class="text-muted d-flex align-items-center">
                      <feather-icon icon="CalendarIcon" size="14" class="mr-1" />
                      Fin: {{ formatearFecha(obra.fechaFinObra) }}
                    </small>
                  </div>
                </b-card-body>
              </b-card>
            </div>
          </div>
          
          <div v-else class="text-center py-5 text-muted">
            <feather-icon icon="BuildingIcon" size="48" class="mb-3" />
            <h5>No hay obras asociadas</h5>
            <p class="mb-3">Este proyecto no tiene obras registradas aún.</p>
            <b-button variant="outline-primary" @click="mostrarModalObra">
              <feather-icon icon="PlusIcon" size="16" class="mr-50" />
              Añadir Primera Obra
            </b-button>
          </div>
        </div>

        <!-- Sección de Actividades -->
        <div class="mt-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h4 class="mb-0">
              <feather-icon icon="ActivityIcon" size="20" class="mr-2" />
              Actividades del Proyecto
              <b-badge variant="success" class="ml-2">{{ proyecto.actividades ? proyecto.actividades.length : 0 }}</b-badge>
            </h4>
            <b-button variant="success" size="sm" @click="mostrarModalActividad">
              <feather-icon icon="PlusIcon" size="16" class="mr-50" />
              Añadir Actividad
            </b-button>
          </div>
          
          <div v-if="proyecto.actividades && proyecto.actividades.length > 0" class="row">
            <div v-for="(actividad, index) in proyecto.actividades" :key="actividad.idActividad" class="col-md-6 col-lg-4 mb-3">
              <b-card class="h-100 actividad-card" no-body>
                <b-card-body class="p-3">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <h6 class="card-title mb-0">
                      {{ actividad.tipoActividad?.nombre || actividad.nombreTipoActividad }}
                    </h6>
                    <b-dropdown variant="link" toggle-class="text-decoration-none p-0" no-caret right>
                      <template #button-content>
                        <feather-icon icon="MoreVerticalIcon" size="16" />
                      </template>
                      <b-dropdown-item @click="editarActividad(actividad, index)">
                        <feather-icon icon="EditIcon" size="14" class="mr-2" />
                        Editar
                      </b-dropdown-item>
                      <b-dropdown-item @click="eliminarActividad(index)" class="text-danger">
                        <feather-icon icon="TrashIcon" size="14" class="mr-2" />
                        Eliminar
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                  
                  <div class="mb-2">
                    <small class="text-muted d-flex align-items-center">
                      <feather-icon icon="CalendarIcon" size="14" class="mr-1" />
                      {{ formatearFecha(actividad.fecha) }}
                    </small>
                  </div>
                  
                  <div class="mb-2">
                    <b-badge 
                      :variant="getEstadoActividadVariant(actividad.estadoActividad?.nombre || actividad.nombreEstadoActividad)" 
                      class="mr-1"
                    >
                      {{ actividad.estadoActividad?.nombre || actividad.nombreEstadoActividad }}
                    </b-badge>
                  </div>
                  
                  <div v-if="actividad.observaciones" class="mb-2">
                    <small class="text-muted">
                      <strong>Observaciones:</strong><br>
                      {{ actividad.observaciones.length > 100 ? actividad.observaciones.substring(0, 100) + '...' : actividad.observaciones }}
                    </small>
                  </div>
                  
                  <div class="mb-2">
                    <small class="text-muted d-flex align-items-center">
                      <feather-icon icon="ClockIcon" size="14" class="mr-1" />
                      <strong>Próxima acción:</strong>
                      <b-badge v-if="actividad.recordatorio" variant="warning" class="ml-2">
                        <feather-icon icon="BellIcon" size="12" class="mr-1" />
                        Recordatorio
                      </b-badge>
                    </small>
                    <small class="text-muted d-block">
                      {{ actividad.proximaAccionTipo?.nombre || actividad.nombreProximaAccionTipo }} - {{ formatearFecha(actividad.proximaAccionFecha) }}
                    </small>
                  </div>
                  
                  <div v-if="actividad.creadoPor" class="mb-2">
                    <small class="text-muted d-flex align-items-center">
                      <feather-icon icon="UserIcon" size="14" class="mr-1" />
                      Creado por: {{ actividad.creadoPor.nombre || actividad.nombreCreadoPor }}
                    </small>
                  </div>
                </b-card-body>
              </b-card>
            </div>
          </div>
          
          <div v-else class="text-center py-5 text-muted">
            <feather-icon icon="ActivityIcon" size="48" class="mb-3" />
            <h5>No hay actividades asociadas</h5>
            <p class="mb-3">Este proyecto no tiene actividades registradas aún.</p>
            <b-button variant="outline-success" @click="mostrarModalActividad">
              <feather-icon icon="PlusIcon" size="16" class="mr-50" />
              Añadir Primera Actividad
            </b-button>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-4">
          <b-button variant="outline-secondary" @click="volver">
            Cancelar
          </b-button>
          <b-button type="submit" variant="primary" :disabled="guardando">
            <feather-icon
              v-if="guardando"
              icon="LoaderIcon"
              size="16"
              class="mr-50"
              animation="spin"
            />
            <feather-icon
              v-else
              icon="SaveIcon"
              size="16"
              class="mr-50"
            />
            {{ guardando ? 'Guardando...' : 'Actualizar Proyecto' }}
          </b-button>
        </div>
      </b-form>
    </b-card>

    <!-- Modal para Agregar/Editar Obra -->
    <b-modal
      id="modal-obra"
      :title="modalObra.editing ? 'Editar Obra' : 'Gestionar Obras'"
      size="lg"
      @hidden="resetModalObra"
    >
      <b-tabs v-model="modalObra.activeTab" content-class="mt-3">
        <!-- Pestaña: Seleccionar Obras Existentes -->
        <b-tab title="Seleccionar Obras" :title-link-class="modalObra.activeTab === 0 ? 'active' : ''">
          <div class="mb-3">
            <b-form-input
              v-model="filtroObras"
              placeholder="Buscar obras..."
              type="search"
            />
          </div>
          
          <div v-if="obrasDisponibles.length > 0" class="obras-list">
            <div
              v-for="(obra, index) in obrasFiltradas"
              :key="index"
              class="obra-item"
              :class="{ 'selected': obraSeleccionada && obraSeleccionada.idObra === obra.idObra }"
              @click="seleccionarObra(obra)"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="flex-grow-1">
                  <h6 class="mb-1">{{ obra.direccion }}</h6>
                  <p class="text-muted mb-1">{{ obra.municipio }}, {{ obra.provincia }}</p>
                  <div class="d-flex gap-2">
                    <b-badge :variant="getObraBadgeVariant(obra.nombreEstadoObra)">
                      {{ obra.nombreEstadoObra }}
                    </b-badge>
                    <b-badge variant="outline-secondary">{{ obra.nombreFase }}</b-badge>
                  </div>
                </div>
                <div class="text-right">
                  <feather-icon 
                    v-if="obraSeleccionada && obraSeleccionada.idObra === obra.idObra"
                    icon="CheckIcon" 
                    size="20" 
                    class="text-success"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="text-center py-4 text-muted">
            <feather-icon icon="SearchIcon" size="48" class="mb-2" />
            <p>No se encontraron obras</p>
          </div>
        </b-tab>

        <!-- Pestaña: Crear Nueva Obra -->
        <b-tab title="Crear Nueva Obra" :title-link-class="modalObra.activeTab === 1 ? 'active' : ''">
          <b-form @submit.prevent="guardarObra">
            <b-row>
              <b-col md="6">
                <b-form-group label="Dirección" label-for="direccion" required>
                  <b-form-input
                    id="direccion"
                    v-model="modalObra.direccion"
                    type="text"
                    placeholder="Ingrese la dirección"
                    required
                    :state="erroresObra.direccion"
                    @input="erroresObra.direccion = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.direccion === false">
                    La dirección es requerida
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Municipio" label-for="municipio" required>
                  <b-form-input
                    id="municipio"
                    v-model="modalObra.municipio"
                    type="text"
                    placeholder="Ingrese el municipio"
                    required
                    :state="erroresObra.municipio"
                    @input="erroresObra.municipio = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.municipio === false">
                    El municipio es requerido
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group label="Provincia" label-for="provincia" required>
                  <b-form-input
                    id="provincia"
                    v-model="modalObra.provincia"
                    type="text"
                    placeholder="Ingrese la provincia"
                    required
                    :state="erroresObra.provincia"
                    @input="erroresObra.provincia = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.provincia === false">
                    La provincia es requerida
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Código Postal" label-for="codigoPostal">
                  <b-form-input
                    id="codigoPostal"
                    v-model="modalObra.codigoPostal"
                    type="number"
                    placeholder="Código postal"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group label="Tipo de Obra" label-for="tipoObra" required>
                  <b-form-select
                    id="tipoObra"
                    v-model="modalObra.idTipoObra"
                    :options="opcionesTiposObra"
                    required
                    :state="erroresObra.idTipoObra"
                    @change="erroresObra.idTipoObra = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.idTipoObra === false">
                    El tipo de obra es requerido
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Estado de Obra" label-for="estadoObra" required>
                  <b-form-select
                    id="estadoObra"
                    v-model="modalObra.idEstadoObra"
                    :options="opcionesEstadosObra"
                    required
                    :state="erroresObra.idEstadoObra"
                    @change="erroresObra.idEstadoObra = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.idEstadoObra === false">
                    El estado de obra es requerido
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group label="Empresa" label-for="empresa" required>
                  <div class="d-flex gap-2">
                    <b-form-select
                      id="empresa"
                      v-model="modalObra.idEmpresa"
                      :options="opcionesEmpresas"
                      required
                      class="flex-grow-1"
                      :state="erroresObra.idEmpresa"
                      @change="erroresObra.idEmpresa = null"
                    />
                    <b-button 
                      variant="outline-primary" 
                      size="sm"
                      @click="mostrarModalEmpresa"
                      title="Crear nueva empresa"
                    >
                      <feather-icon icon="PlusIcon" size="16" />
                    </b-button>
                  </div>
                  <b-form-invalid-feedback v-if="erroresObra.idEmpresa === false">
                    La empresa es requerida
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Referencia de Obra" label-for="referenciaObra">
                  <b-form-select
                    id="referenciaObra"
                    v-model="modalObra.idReferenciaObra"
                    :options="opcionesReferenciasObra"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group label="Fase" label-for="fase" required>
                  <b-form-select
                    id="fase"
                    v-model="modalObra.idFase"
                    :options="opcionesFases"
                    required
                    :state="erroresObra.idFase"
                    @change="erroresObra.idFase = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.idFase === false">
                    La fase es requerida
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Referencia Externa" label-for="referenciaExterna">
                  <b-form-input
                    id="referenciaExterna"
                    v-model="modalObra.referenciaExterna"
                    type="text"
                    placeholder="Referencia externa"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <b-form-group label="Fecha de Inicio" label-for="fechaInicioObra" required>
                  <b-form-datepicker
                    id="fechaInicioObra"
                    v-model="modalObra.fechaInicioObra"
                    required
                    :state="erroresObra.fechaInicioObra"
                    @input="erroresObra.fechaInicioObra = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.fechaInicioObra === false">
                    La fecha de inicio es requerida
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Fecha de Fin" label-for="fechaFinObra">
                  <b-form-datepicker
                    id="fechaFinObra"
                    v-model="modalObra.fechaFinObra"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-group label="Descripción" label-for="descripcionObra">
              <b-form-textarea
                id="descripcionObra"
                v-model="modalObra.descripcion"
                rows="3"
                placeholder="Descripción de la obra..."
              />
            </b-form-group>

            <b-form-group label="Magnitudes" label-for="magnitudes">
              <b-form-input
                id="magnitudes"
                v-model="modalObra.magnitudes"
                type="text"
                placeholder="Magnitudes de la obra"
              />
            </b-form-group>
          </b-form>
        </b-tab>
      </b-tabs>
      
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="$bvModal.hide('modal-obra')">
          Cancelar
        </b-button>
        <b-button 
          v-if="modalObra.activeTab === 0 && obraSeleccionada"
          variant="primary" 
          @click="agregarObraSeleccionada"
        >
          Agregar Obra Seleccionada
        </b-button>
        <b-button 
          v-else-if="modalObra.activeTab === 1"
          variant="success" 
          @click="guardarObra"
        >
          {{ modalObra.editing ? 'Actualizar' : 'Crear' }} Obra
        </b-button>
      </template>
    </b-modal>

    <!-- Modal para Actividad -->
    <b-modal
      id="modal-actividad"
      :title="modalActividad.esEdicion ? 'Editar Actividad' : 'Añadir Actividad'"
      size="lg"
      @hidden="resetearModalActividad"
    >
      <b-form @submit.prevent="guardarActividad">
        <b-row>
          <b-col md="6">
            <b-form-group label="Tipo de Actividad" label-for="tipoActividad" required>
              <b-form-select
                id="tipoActividad"
                v-model="modalActividad.idTipoActividad"
                :options="opcionesTiposActividad"
                required
                :state="erroresActividad.idTipoActividad"
                @change="erroresActividad.idTipoActividad = null"
              />
              <b-form-invalid-feedback v-if="erroresActividad.idTipoActividad === false">
                El tipo de actividad es requerido
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Estado de Actividad" label-for="estadoActividad" required>
              <b-form-select
                id="estadoActividad"
                v-model="modalActividad.idEstadoActividad"
                :options="opcionesEstadosActividad"
                required
                :state="erroresActividad.idEstadoActividad"
                @change="erroresActividad.idEstadoActividad = null"
              />
              <b-form-invalid-feedback v-if="erroresActividad.idEstadoActividad === false">
                El estado de actividad es requerido
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Fecha" label-for="fechaActividad" required>
              <b-form-datepicker
                id="fechaActividad"
                v-model="modalActividad.fecha"
                required
                :state="erroresActividad.fecha"
                @input="erroresActividad.fecha = null"
              />
              <b-form-invalid-feedback v-if="erroresActividad.fecha === false">
                La fecha es requerida
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Próxima Acción" label-for="proximaAccionTipo" required>
              <b-form-select
                id="proximaAccionTipo"
                v-model="modalActividad.idProximaAccionTipo"
                :options="opcionesAccionesActividad"
                required
                :state="erroresActividad.idProximaAccionTipo"
                @change="erroresActividad.idProximaAccionTipo = null"
              />
              <b-form-invalid-feedback v-if="erroresActividad.idProximaAccionTipo === false">
                La próxima acción es requerida
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Fecha Próxima Acción" label-for="proximaAccionFecha" required>
              <b-form-datepicker
                id="proximaAccionFecha"
                v-model="modalActividad.proximaAccionFecha"
                required
                :state="erroresActividad.proximaAccionFecha"
                @input="erroresActividad.proximaAccionFecha = null"
              />
              <b-form-invalid-feedback v-if="erroresActividad.proximaAccionFecha === false">
                La fecha de próxima acción es requerida
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Recordatorio" label-for="recordatorio">
              <b-form-checkbox
                id="recordatorio"
                v-model="modalActividad.recordatorio"
                switch
              >
                Activar recordatorio
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Observaciones" label-for="observacionesActividad">
          <b-form-textarea
            id="observacionesActividad"
            v-model="modalActividad.observaciones"
            rows="3"
            placeholder="Observaciones de la actividad..."
          />
        </b-form-group>

        <!-- Sección de Documentos -->
        <b-form-group label="Documentos Adjuntos">
          <div v-if="documentosActividad.length === 0" class="text-center text-muted py-3">
            <feather-icon icon="FileIcon" size="24" class="mb-2" />
            <p>No hay documentos adjuntos</p>
          </div>
          <div v-else>
            <div
              v-for="(documento, index) in documentosActividad"
              :key="index"
              class="documento-item d-flex justify-content-between align-items-center mb-2 p-2 border rounded"
            >
              <div class="documento-info">
                <div class="documento-nombre">
                  <feather-icon icon="FileIcon" size="16" class="mr-2" />
                  {{ documento.nombreArchivo }}
                </div>
                <small class="text-muted">
                  Categoría: {{ documento.nombreCategoriaArchivo || documento.nombreCategoria || 'Sin categoría' }} | 
                  Versión: {{ documento.version || '1.0' }}
                  <span v-if="documento.tamaño">
                    | Tamaño: {{ (documento.tamaño / 1024).toFixed(1) }} KB
                  </span>
                </small>
              </div>
              <div class="d-flex gap-1">
                <b-button
                  v-if="documento.idDocumento"
                  variant="outline-primary"
                  size="sm"
                  @click="descargarDocumento(documento.idDocumento, documento.nombreArchivo)"
                  v-b-tooltip.hover
                  title="Descargar documento"
                >
                  <feather-icon icon="DownloadIcon" size="14" />
                </b-button>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="eliminarDocumento(index)"
                  v-b-tooltip.hover
                  title="Eliminar documento"
                >
                  <feather-icon icon="TrashIcon" size="14" />
                </b-button>
              </div>
            </div>
          </div>
          
          <div class="d-flex gap-2 mt-2">
            <b-button
              variant="outline-primary"
              size="sm"
              @click="mostrarSubidaDocumentos = true"
            >
              <feather-icon icon="PlusIcon" size="14" class="mr-1" />
              Agregar Documento
            </b-button>
            <b-button
              variant="outline-info"
              size="sm"
              @click="recargarDocumentos"
              v-if="modalActividad.esEdicion"
            >
              <feather-icon icon="RefreshCwIcon" size="14" class="mr-1" />
              Recargar
            </b-button>
          </div>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="cerrarModalActividad">
          Cancelar
        </b-button>
        <b-button variant="primary" @click="guardarActividad" :disabled="guardandoActividad">
          <feather-icon
            v-if="guardandoActividad"
            icon="LoaderIcon"
            size="16"
            class="mr-50"
            animation="spin"
          />
          {{ guardandoActividad ? 'Guardando...' : (modalActividad.esEdicion ? 'Actualizar' : 'Crear') }}
        </b-button>
      </template>
    </b-modal>

    <!-- Modal para Subir Documentos -->
    <b-modal
      id="modal-subir-documento"
      title="Agregar Documento"
      size="lg"
      v-model="mostrarSubidaDocumentos"
      @hidden="resetSubidaDocumentos"
    >
      <SubidaDocumentos
        :id-actividad="modalActividad.esEdicion ? modalActividad.idActividad : null"
        :id-usuario="usuarioActual.idUsuario"
        :modo-temporal="!modalActividad.esEdicion"
        @documento-subido="onDocumentoSubido"
        @finalizar-subida="onFinalizarSubida"
      />
    </b-modal>

    <!-- Modal para Crear Nueva Empresa -->
    <b-modal
      id="modal-empresa"
      title="Nueva Empresa"
      size="lg"
      @hidden="resetModalEmpresa"
    >
      <b-form @submit.prevent="guardarEmpresa">
        <b-row>
          <b-col md="6">
            <b-form-group label="Nombre de la Empresa" required>
              <b-form-input 
                v-model="modalEmpresa.nombre" 
                placeholder="Ingrese el nombre de la empresa"
                required 
              />
            </b-form-group>

            <b-form-group label="CIF" required>
              <b-form-input 
                v-model="modalEmpresa.cif" 
                placeholder="Ingrese el CIF"
                required 
              />
            </b-form-group>

            <b-form-group label="Tipo de Empresa" required>
              <b-form-select 
                v-model="modalEmpresa.idTipoEmpresa" 
                :options="tiposEmpresas"
                value-field="value"
                text-field="text"
                placeholder="Seleccione un tipo"
                required
              />
            </b-form-group>

            <b-form-group label="Teléfono">
              <b-form-input 
                v-model="modalEmpresa.telefono" 
                placeholder="Ingrese el teléfono"
              />
            </b-form-group>

            <b-form-group label="Email">
              <b-form-input 
                v-model="modalEmpresa.email" 
                type="email"
                placeholder="Ingrese el email"
              />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Dirección" required>
              <b-form-textarea 
                v-model="modalEmpresa.direccion" 
                placeholder="Ingrese la dirección completa"
                rows="3"
                required 
              />
            </b-form-group>

            <b-form-group label="Sitio Web">
              <b-form-input 
                v-model="modalEmpresa.web" 
                placeholder="https://www.ejemplo.com"
              />
            </b-form-group>

            <b-form-group label="Observaciones">
              <b-form-textarea 
                v-model="modalEmpresa.observaciones" 
                placeholder="Observaciones adicionales"
                rows="3"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="$bvModal.hide('modal-empresa')">
          Cancelar
        </b-button>
        <b-button variant="success" @click="guardarEmpresa" :disabled="guardandoEmpresa">
          <feather-icon
            v-if="guardandoEmpresa"
            icon="LoaderIcon"
            size="16"
            class="mr-50"
            animation="spin"
          />
          {{ guardandoEmpresa ? 'Guardando...' : 'Crear Empresa' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { EstadoProyectoService } from '@/services/estadoProyectoService'
import { ProyectoService } from '@/services/proyectoService'
import { ObraService } from '@/services/obraService'
import { ActividadService } from '@/services/actividadService'
import { DocumentoService } from '@/services/documentoService'
import UsuarioService from '@/services/usuarioService'
import { TipoActividadService } from '@/services/tipoActividadService'
import { EstadoActividadService } from '@/services/estadoActividadService'
import { AccionActividadService } from '@/services/accionActividadService'
import { FaseService } from '@/services/faseService'
import { TipoObraService } from '@/services/tipoObraService'
import { EstadoObraService } from '@/services/estadoObraService'
import { ReferenciaObraService } from '@/services/referenciaObraService'
import { EmpresaService } from '@/services/empresaService'
import { TipoEmpresaService } from '@/services/tipoEmpresaService'
import SubidaDocumentos from '@/components/SubidaDocumentos.vue'
import authService from '@/services/authService'
import { 
  generarEnlaceGoogleCalendar, 
  generarEnlaceOutlookCalendar, 
  descargarArchivoICS,
  crearEventoDesdeActividad 
} from '@/utils/calendarUtils'

export default {
  name: 'ProyectosEditar',
  data() {
    return {
      cargando: true,
      guardando: false,
      guardandoObra: false,
      guardandoActividad: false,
      proyecto: {
        idProyecto: null,
        nombre: '',
        fechaInicio: '',
        fechaFin: '',
        presupuesto: '',
        estado: 'Pendiente',
        descripcion: '',
        responsable: '',
        obras: [],
        actividades: [],
      },
      opcionesEstado: [],
      estadosProyectos: [],
      opcionesResponsables: [{ value: '', text: 'Seleccionar responsable' }],
      // Datos para obras
      opcionesTiposObra: [{ value: '', text: 'Seleccionar tipo de obra' }],
      opcionesEstadosObra: [{ value: '', text: 'Seleccionar estado' }],
      opcionesFases: [{ value: '', text: 'Seleccionar fase' }],
      opcionesEmpresas: [{ value: '', text: 'Seleccionar empresa' }],
      opcionesReferenciasObra: [{ value: '', text: 'Seleccionar referencia' }],
      // Datos para actividades
      opcionesTiposActividad: [{ value: '', text: 'Seleccionar tipo de actividad' }],
      opcionesEstadosActividad: [{ value: '', text: 'Seleccionar estado' }],
      opcionesAccionesActividad: [{ value: '', text: 'Seleccionar acción' }],
      // Modal para obra
      modalObra: {
        editing: false,
        activeTab: 0,
        indice: -1,
        idObra: null,
        direccion: '',
        municipio: '',
        provincia: '',
        codigoPostal: null,
        idTipoObra: null,
        idEstadoObra: null,
        idFase: null,
        idEmpresa: null,
        idReferenciaObra: null,
        referenciaExterna: '',
        fechaInicioObra: '',
        fechaFinObra: '',
        descripcion: '',
        magnitudes: '',
      },
      // Errores de validación para el formulario de obra
      erroresObra: {
        direccion: null,
        municipio: null,
        provincia: null,
        idTipoObra: null,
        idEstadoObra: null,
        idFase: null,
        fechaInicioObra: null,
        idEmpresa: null,
      },
      // Variables para selección de obras
      obrasDisponibles: [],
      filtroObras: '',
      obraSeleccionada: null,
      // Catálogos para obras
      referenciasObras: [],
      estadosObras: [],
      tiposObras: [],
      // Modal para actividad
      modalActividad: {
        esEdicion: false,
        indice: -1,
        idActividad: null,
        idTipoActividad: null,
        idEstadoActividad: null,
        fecha: '',
        proximaAccionFecha: '',
        idProximaAccionTipo: null,
        observaciones: '',
        recordatorio: false,
      },
      // Variables para gestión de documentos
      documentosActividad: [],
      mostrarSubidaDocumentos: false,
      // Errores de validación para el formulario de actividad
      erroresActividad: {
        idTipoActividad: null,
        idEstadoActividad: null,
        fecha: null,
        idProximaAccionTipo: null,
        proximaAccionFecha: null,
      },
      // Modal para empresas
      guardandoEmpresa: false,
      modalEmpresa: {
        nombre: '',
        direccion: '',
        idTipoEmpresa: '',
        cif: '',
        web: '',
        telefono: '',
        email: '',
        observaciones: '',
      },
      tiposEmpresas: [],
      usuarioActual: {
        idUsuario: null
      },
    }
  },
  components: {
    SubidaDocumentos
  },
  computed: {
    obrasFiltradas() {
      if (!this.filtroObras) return this.obrasDisponibles
      
      const filtro = this.filtroObras.toLowerCase()
      return this.obrasDisponibles.filter(obra => 
        obra.direccion?.toLowerCase().includes(filtro) ||
        obra.municipio?.toLowerCase().includes(filtro) ||
        obra.provincia?.toLowerCase().includes(filtro) ||
        obra.nombreEstadoObra?.toLowerCase().includes(filtro) ||
        obra.nombreFase?.toLowerCase().includes(filtro)
      )
    }
  },
  mounted() {
    this.cargarEstadosProyectos()
    this.cargarResponsables()
    this.cargarDatosCatalogos()
    this.cargarProyecto()
    this.cargarUsuarioActual()
  },
  methods: {
    async cargarEstadosProyectos() {
      try {
        this.estadosProyectos = await EstadoProyectoService.getAll()
        this.opcionesEstado = this.estadosProyectos.map(estado => ({
          value: estado.nombre,
          text: estado.nombre
        }))
      } catch (error) {
        console.error('Error al cargar estados de proyectos:', error)
        this.$toast?.error('Error al cargar los estados de proyectos')
      }
    },
    async cargarResponsables() {
      try {
        console.log('📡 Cargando responsables...')
        const usuarios = await UsuarioService.getAllUsuarios()
        console.log('✅ Responsables cargados:', usuarios)
        
        this.opcionesResponsables = [
          { value: '', text: 'Seleccionar responsable' },
          ...usuarios.map(usuario => ({
            value: usuario.idUsuario,
            text: `${usuario.nombre} ${usuario.primerApellido} ${usuario.segundoApellido || ''}`.trim()
          }))
        ]
      } catch (error) {
        console.error('Error al cargar responsables:', error)
        this.$toast?.error('Error al cargar los responsables')
        // Usar datos de ejemplo si hay error
        this.opcionesResponsables = [
          { value: '', text: 'Seleccionar responsable' },
          { value: 1, text: 'Juan Pérez' },
          { value: 2, text: 'María García' },
          { value: 3, text: 'Carlos López' }
        ]
      }
    },
    async cargarProyecto() {
      this.cargando = true
      try {
        const proyectoId = parseInt(this.$route.params.id)
        console.log('🔍 Cargando proyecto con ID:', proyectoId)
        
        if (!proyectoId || isNaN(proyectoId)) {
          throw new Error('ID de proyecto inválido')
        }
        
        // Verificar si el proyecto existe
        console.log('📡 Verificando existencia del proyecto...')
        const existe = await ProyectoService.existsById(proyectoId)
        if (!existe) {
          throw new Error('El proyecto no existe')
        }
        
        // Cargar datos del proyecto
        console.log('📡 Llamando a ProyectoService.getById...')
        const proyecto = await ProyectoService.getById(proyectoId)
        console.log('✅ Proyecto obtenido:', proyecto)
        
        if (!proyecto) {
          throw new Error('No se pudo obtener el proyecto')
        }
        
        // Cargar obras del proyecto
        console.log('📡 Llamando a ProyectoService.getObrasByProyecto...')
        let obras = []
        try {
          obras = await ProyectoService.getObrasByProyecto(proyectoId)
          console.log('✅ Obras obtenidas:', obras)
        } catch (obraError) {
          console.warn('⚠️ Error al cargar obras, usando datos de ejemplo:', obraError)
          obras = this.getObrasEjemplo()
        }
        
        // Cargar actividades del proyecto
        console.log('📡 Llamando a ProyectoService.getActividadesByProyecto...')
        let actividades = []
        try {
          actividades = await ProyectoService.getActividadesByProyecto(proyectoId)
          console.log('✅ Actividades obtenidas:', actividades)
        } catch (actividadError) {
          console.warn('⚠️ Error al cargar actividades, usando datos de ejemplo:', actividadError)
          actividades = this.getActividadesEjemplo()
        }
        
        this.proyecto = {
          ...proyecto,
          // Mapear campos para los v-model de los selects
          responsable: proyecto.idResponsable,
          estado: proyecto.nombreEstadoProyecto,
          obras: obras || [],
          actividades: actividades || []
        }
        
        console.log('🎯 Proyecto final cargado:', this.proyecto)
      } catch (error) {
        console.error('❌ Error al cargar el proyecto:', error)
        console.error('❌ Detalles del error:', error.response?.data || error.message)
        
        // Mostrar datos de ejemplo si hay error de conexión
        if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
          console.log('🔄 Cargando datos de ejemplo debido a error de red...')
          this.cargarDatosEjemplo()
        } else {
          this.$toast?.error(`Error al cargar el proyecto: ${error.message}`)
        }
      } finally {
        this.cargando = false
      }
    },
    cargarDatosEjemplo() {
      // Datos de ejemplo para cuando el backend no esté disponible
      this.proyecto = {
        idProyecto: parseInt(this.$route.params.id),
        nombre: 'Proyecto de Ejemplo',
        fechaInicio: '2024-01-15',
        fechaFin: '2024-03-15',
        presupuesto: 50000,
        estado: 'En Progreso',
        descripcion: 'Proyecto de ejemplo para demostración.',
        responsable: 1,
        obras: [
          {
            idObra: 1,
            direccion: 'Calle Mayor 123',
            municipio: 'Madrid',
            provincia: 'Madrid',
            codigoPostal: 28001,
            nombreEstadoObra: 'En Progreso',
            nombreFase: 'Construcción',
            nombreTipoObra: 'Residencial',
            fechaInicioObra: '2024-01-15',
            fechaFinObra: '2024-06-15',
            descripcion: 'Edificio residencial de 5 plantas',
            magnitudes: '2000 m²'
          }
        ],
        actividades: [
          {
            idActividad: 1,
            tipoActividad: { idTipoActividad: 1, nombre: 'Reunión de coordinación' },
            fecha: '2024-01-20',
            estadoActividad: { idEstadoActividad: 1, nombre: 'Completada' },
            observaciones: 'Reunión inicial con el equipo',
            proximaAccionFecha: '2024-01-25',
            proximaAccionTipo: { idAccionActividad: 1, nombre: 'Seguimiento' },
            recordatorio: true
          }
        ]
      }
      this.$toast?.warning('Mostrando datos de ejemplo - Backend no disponible')
    },
    getObrasEjemplo() {
      return [
        {
          idObra: 1,
          direccion: 'Calle Mayor 123',
          municipio: 'Madrid',
          provincia: 'Madrid',
          codigoPostal: 28001,
          nombreEstadoObra: 'En Progreso',
          nombreFase: 'Construcción',
          nombreTipoObra: 'Residencial',
          fechaInicioObra: '2024-01-15',
          fechaFinObra: '2024-06-15',
          descripcion: 'Edificio residencial de 5 plantas',
          magnitudes: '2000 m²'
        }
      ]
    },
    getActividadesEjemplo() {
      return [
        {
          idActividad: 1,
          tipoActividad: { idTipoActividad: 1, nombre: 'Reunión de coordinación' },
          fecha: '2024-01-20',
          estadoActividad: { idEstadoActividad: 1, nombre: 'Completada' },
          observaciones: 'Reunión inicial con el equipo',
          proximaAccionFecha: '2024-01-25',
          proximaAccionTipo: { idAccionActividad: 1, nombre: 'Seguimiento' },
          recordatorio: true
        }
      ]
    },
    async cargarDatosCatalogos() {
      try {
        // Cargar tipos de obra
        const tiposObra = await TipoObraService.getAll()
        this.opcionesTiposObra = [
          { value: '', text: 'Seleccionar tipo de obra' },
          ...tiposObra.map(tipo => ({
            value: tipo.idTipoObra,
            text: tipo.nombre
          }))
        ]

        // Cargar estados de obra
        const estadosObra = await EstadoObraService.getAll()
        this.opcionesEstadosObra = [
          { value: '', text: 'Seleccionar estado' },
          ...estadosObra.map(estado => ({
            value: estado.idEstadoObra,
            text: estado.nombre
          }))
        ]

        // Cargar fases
        const fases = await FaseService.getAll()
        this.opcionesFases = [
          { value: '', text: 'Seleccionar fase' },
          ...fases.map(fase => ({
            value: fase.idFase,
            text: fase.nombre
          }))
        ]

        // Cargar tipos de actividad
        const tiposActividad = await TipoActividadService.getAll()
        this.opcionesTiposActividad = [
          { value: '', text: 'Seleccionar tipo de actividad' },
          ...tiposActividad.map(tipo => ({
            value: tipo.idTipoActividad,
            text: tipo.nombre
          }))
        ]

        // Cargar estados de actividad
        const estadosActividad = await EstadoActividadService.getAll()
        this.opcionesEstadosActividad = [
          { value: '', text: 'Seleccionar estado' },
          ...estadosActividad.map(estado => ({
            value: estado.idEstadoActividad,
            text: estado.nombre
          }))
        ]

        // Cargar acciones de actividad
        const accionesActividad = await AccionActividadService.getAll()
        this.opcionesAccionesActividad = [
          { value: '', text: 'Seleccionar acción' },
          ...accionesActividad.map(accion => ({
            value: accion.idAccionActividad,
            text: accion.nombre
          }))
        ]

        // Cargar tipos de empresas
        const tiposEmpresas = await TipoEmpresaService.getAll()
        this.tiposEmpresas = tiposEmpresas.map(tipo => ({
          value: tipo.idTipoEmpresa,
          text: tipo.nombre
        }))
      } catch (error) {
        console.error('Error al cargar catálogos:', error)
        this.$toast?.error('Error al cargar los catálogos')
      }
    },
    formatearFecha(fecha) {
      if (!fecha) return ''
      return new Date(fecha).toLocaleDateString('es-ES')
    },
    // Métodos para obras
    async mostrarModalObra() {
      this.resetModalObra()
      await this.cargarDatosObras()
      this.$bvModal.show('modal-obra')
    },
    
    async cargarDatosObras() {
      try {
        console.log('Cargando obras para el modal...')
        
        // Cargar obras usando el mismo método que el listado
        this.obrasDisponibles = await ObraService.getAll()
        
        // Cargar catálogos necesarios para el formulario de obra
        await Promise.all([
          this.obtenerReferenciasObras(),
          this.obtenerFases(),
          this.obtenerEstadosObras(),
          this.obtenerTiposObras(),
          this.obtenerEmpresas()
        ])
        
        console.log('Datos de obras cargados correctamente')
        console.log('Obras disponibles:', this.obrasDisponibles.length)
        console.log('Referencias:', this.referenciasObras.length)
        console.log('Fases:', this.opcionesFases.length)
        console.log('Estados:', this.opcionesEstadosObra.length)
        console.log('Tipos:', this.opcionesTiposObra.length)
      } catch (error) {
        console.error('Error al cargar datos de obras:', error)
        this.$toast?.error('Error al cargar los datos de obras')
      }
    },
    editarObra(obra, index) {
      // Limpiar errores de validación
      this.erroresObra = {
        direccion: null,
        municipio: null,
        provincia: null,
        idTipoObra: null,
        idEstadoObra: null,
        idFase: null,
        fechaInicioObra: null,
        idEmpresa: null,
      }
      this.modalObra = {
        editing: true,
        activeTab: 1, // Ir directamente a la pestaña de crear/editar
        indice: index,
        idObra: obra.idObra,
        direccion: obra.direccion,
        municipio: obra.municipio,
        provincia: obra.provincia,
        codigoPostal: obra.codigoPostal,
        idTipoObra: obra.idTipoObra,
        idEstadoObra: obra.idEstadoObra,
        idFase: obra.idFase,
        idEmpresa: obra.idEmpresa || obra.empresa?.idEmpresa,
        idReferenciaObra: obra.idReferenciaObra || obra.referenciaObra?.idReferenciaObra,
        referenciaExterna: obra.referenciaExterna,
        fechaInicioObra: obra.fechaInicioObra,
        fechaFinObra: obra.fechaFinObra,
        descripcion: obra.descripcion,
        magnitudes: obra.magnitudes,
      }
      this.$bvModal.show('modal-obra')
    },
    
    // Métodos para selección de obras
    seleccionarObra(obra) {
      this.obraSeleccionada = obra
    },
    
    agregarObraSeleccionada() {
      if (!this.obraSeleccionada) return
      
      // Verificar si la obra ya está agregada al proyecto
      const yaExiste = this.proyecto.obras.some(obra => obra.idObra === this.obraSeleccionada.idObra)
      if (yaExiste) {
        this.$toast?.error('Esta obra ya está agregada al proyecto')
        return
      }
      
      // Agregar la obra al proyecto
      this.proyecto.obras.push({
        ...this.obraSeleccionada,
        // Asegurar que tenemos los campos necesarios
        nombreEstadoObra: this.obraSeleccionada.nombreEstadoObra || this.obraSeleccionada.estadoObra?.nombre,
        nombreFase: this.obraSeleccionada.nombreFase || this.obraSeleccionada.fase?.nombre,
        nombreEmpresa: this.obraSeleccionada.nombreEmpresa || this.obraSeleccionada.empresa?.nombre
      })
      
      this.$toast?.success('Obra agregada al proyecto correctamente')
      this.$bvModal.hide('modal-obra')
    },
    
    getObraBadgeVariant(estado) {
      const variantes = {
        'Pendiente': 'secondary',
        'En Progreso': 'info',
        'Completada': 'success',
        'Cancelada': 'danger',
        'En Planificación': 'warning',
        'Pausada': 'secondary'
      }
      return variantes[estado] || 'secondary'
    },
    async guardarObra() {
      // Limpiar errores previos
      this.erroresObra = {
        direccion: null,
        municipio: null,
        provincia: null,
        idTipoObra: null,
        idEstadoObra: null,
        idFase: null,
        fechaInicioObra: null,
        idEmpresa: null,
      }
      
      // Validaciones requeridas y marcar campos con errores
      let hayErrores = false
      
      if (!this.modalObra.direccion.trim()) {
        this.erroresObra.direccion = false
        hayErrores = true
      }
      if (!this.modalObra.municipio.trim()) {
        this.erroresObra.municipio = false
        hayErrores = true
      }
      if (!this.modalObra.provincia.trim()) {
        this.erroresObra.provincia = false
        hayErrores = true
      }
      if (!this.modalObra.idTipoObra) {
        this.erroresObra.idTipoObra = false
        hayErrores = true
      }
      if (!this.modalObra.idEstadoObra) {
        this.erroresObra.idEstadoObra = false
        hayErrores = true
      }
      if (!this.modalObra.idFase) {
        this.erroresObra.idFase = false
        hayErrores = true
      }
      if (!this.modalObra.fechaInicioObra) {
        this.erroresObra.fechaInicioObra = false
        hayErrores = true
      }
      if (!this.modalObra.idEmpresa) {
        this.erroresObra.idEmpresa = false
        hayErrores = true
      }
      
      if (hayErrores) {
        this.$toast?.error('Por favor, complete todos los campos requeridos')
        // Hacer scroll al primer campo con error
        this.$nextTick(() => {
          const primerError = document.querySelector('.is-invalid')
          if (primerError) {
            primerError.scrollIntoView({ behavior: 'smooth', block: 'center' })
            primerError.focus()
          }
        })
        return
      }

      this.guardandoObra = true
      try {
        const obraData = {
          direccion: this.modalObra.direccion,
          municipio: this.modalObra.municipio,
          provincia: this.modalObra.provincia,
          codigoPostal: this.modalObra.codigoPostal,
          idTipoObra: this.modalObra.idTipoObra,
          idEstadoObra: this.modalObra.idEstadoObra,
          idFase: this.modalObra.idFase,
          idEmpresa: this.modalObra.idEmpresa,
          idReferenciaObra: this.modalObra.idReferenciaObra,
          referenciaExterna: this.modalObra.referenciaExterna,
          fechaInicioObra: this.modalObra.fechaInicioObra,
          fechaFinObra: this.modalObra.fechaFinObra,
          descripcion: this.modalObra.descripcion,
          magnitudes: this.modalObra.magnitudes,
        }

        if (this.modalObra.editing) {
          // Actualizar obra existente
          const obraActualizada = await ObraService.update(this.modalObra.idObra, obraData)
          this.$set(this.proyecto.obras, this.modalObra.indice, obraActualizada)
        } else {
          // Crear nueva obra
          const nuevaObra = await ObraService.create(obraData)
          this.proyecto.obras.push(nuevaObra)
        }

        this.$toast?.success(this.modalObra.editing ? 'Obra actualizada correctamente' : 'Obra creada correctamente')
        this.$bvModal.hide('modal-obra')
      } catch (error) {
        console.error('Error al guardar obra:', error)
        this.$toast?.error('Error al guardar la obra')
      } finally {
        this.guardandoObra = false
      }
    },
    async eliminarObra(index) {
      this.$bvModal.msgBoxConfirm('¿Estás seguro de que quieres eliminar esta obra?', {
        title: 'Confirmar eliminación',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Eliminar',
        cancelTitle: 'Cancelar',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      }).then(async (value) => {
        if (value) {
          try {
            const obra = this.proyecto.obras[index]
            await ObraService.delete(obra.idObra)
            this.proyecto.obras.splice(index, 1)
            this.$toast?.success('Obra eliminada correctamente')
          } catch (error) {
            const statusCode = error?.response?.status
            const responseData = error?.response?.data
            
            // Si el backend devuelve un 409 (Conflict), es un error de integridad de datos
            if (statusCode === 409) {
              // Intentar obtener el mensaje de diferentes formas
              let errorMessage = null
              
              if (responseData) {
                if (typeof responseData === 'string') {
                  errorMessage = responseData
                } else if (responseData.message) {
                  errorMessage = responseData.message
                } else if (responseData.error) {
                  errorMessage = responseData.error
                }
              }
              
              // Si no se encontró mensaje, usar el mensaje por defecto
              if (!errorMessage) {
                errorMessage = 'No se puede eliminar la obra porque tiene registros asociados. Por favor, elimine primero los registros relacionados.'
              }
              
              this.$toast?.error(errorMessage)
            } else if (statusCode === 404) {
              this.$toast?.error('La obra no existe o ya ha sido eliminada')
            } else {
              console.error('Error al eliminar obra:', error)
              this.$toast?.error('Error al eliminar la obra')
            }
          }
        }
      })
    },
    resetModalObra() {
      this.modalObra = {
        editing: false,
        activeTab: 0,
        indice: -1,
        idObra: null,
        direccion: '',
        municipio: '',
        provincia: '',
        codigoPostal: null,
        idTipoObra: null,
        idEstadoObra: null,
        idFase: null,
        idEmpresa: null,
        idReferenciaObra: null,
        referenciaExterna: '',
        fechaInicioObra: '',
        fechaFinObra: '',
        descripcion: '',
        magnitudes: '',
      }
      // Limpiar errores de validación
      this.erroresObra = {
        direccion: null,
        municipio: null,
        provincia: null,
        idTipoObra: null,
        idEstadoObra: null,
        idFase: null,
        fechaInicioObra: null,
        idEmpresa: null,
      }
      this.obraSeleccionada = null
      this.filtroObras = ''
    },
    
    // Métodos para cargar catálogos de obras
    async obtenerReferenciasObras() {
      try {
        this.referenciasObras = await ReferenciaObraService.getAll()
        this.opcionesReferenciasObra = [
          { value: '', text: 'Seleccionar referencia' },
          ...this.referenciasObras.map(ref => ({
            value: ref.idReferenciaObra,
            text: ref.nombre
          }))
        ]
        console.log('Referencias de obras cargadas:', this.referenciasObras.length)
      } catch (error) {
        console.error('Error al cargar referencias de obras:', error)
      }
    },
    
    async obtenerEmpresas() {
      try {
        const empresas = await EmpresaService.getAll()
        this.opcionesEmpresas = [
          { value: '', text: 'Seleccionar empresa' },
          ...empresas.map(empresa => ({
            value: empresa.idEmpresa,
            text: empresa.nombre
          }))
        ]
        console.log('Empresas cargadas:', this.opcionesEmpresas.length)
      } catch (error) {
        console.error('Error al cargar empresas:', error)
      }
    },
    
    async obtenerFases() {
      try {
        const fases = await FaseService.getAll()
        this.opcionesFases = [
          { value: '', text: 'Seleccionar fase' },
          ...fases.map(fase => ({
            value: fase.idFase,
            text: fase.nombre
          }))
        ]
        console.log('Fases cargadas:', this.opcionesFases.length)
      } catch (error) {
        console.error('Error al cargar fases:', error)
      }
    },
    
    async obtenerEstadosObras() {
      try {
        const estados = await EstadoObraService.getAll()
        this.opcionesEstadosObra = [
          { value: '', text: 'Seleccionar estado' },
          ...estados.map(estado => ({
            value: estado.idEstadoObra,
            text: estado.nombre
          }))
        ]
        console.log('Estados de obras cargados:', this.opcionesEstadosObra.length)
      } catch (error) {
        console.error('Error al cargar estados de obras:', error)
      }
    },
    
    async obtenerTiposObras() {
      try {
        const tipos = await TipoObraService.getAll()
        this.opcionesTiposObra = [
          { value: '', text: 'Seleccionar tipo de obra' },
          ...tipos.map(tipo => ({
            value: tipo.idTipoObra,
            text: tipo.nombre
          }))
        ]
        console.log('Tipos de obras cargados:', this.opcionesTiposObra.length)
      } catch (error) {
        console.error('Error al cargar tipos de obras:', error)
      }
    },
    
    cerrarModalObra() {
      this.$bvModal.hide('modal-obra')
    },
    // Métodos para actividades
    cargarUsuarioActual() {
      const usuarioAutenticado = authService.getCurrentUser()
      if (usuarioAutenticado) {
        const idUsuarioAutenticado = usuarioAutenticado.idUsuario || usuarioAutenticado.id
        if (idUsuarioAutenticado) {
          this.usuarioActual = {
            idUsuario: idUsuarioAutenticado
          }
        }
      }
    },
    mostrarModalActividad() {
      this.resetearModalActividad()
      this.$bvModal.show('modal-actividad')
    },
    async editarActividad(actividad, index) {
      // Limpiar errores de validación
      this.erroresActividad = {
        idTipoActividad: null,
        idEstadoActividad: null,
        fecha: null,
        idProximaAccionTipo: null,
        proximaAccionFecha: null,
      }
      
      this.modalActividad = {
        esEdicion: true,
        indice: index,
        idActividad: actividad.idActividad,
        idTipoActividad: actividad.tipoActividad.idTipoActividad,
        idEstadoActividad: actividad.estadoActividad.idEstadoActividad,
        fecha: actividad.fecha,
        proximaAccionFecha: actividad.proximaAccionFecha,
        idProximaAccionTipo: actividad.proximaAccionTipo.idAccionActividad,
        observaciones: actividad.observaciones,
        recordatorio: actividad.recordatorio,
      }

      // Cargar documentos de la actividad
      try {
        console.log('🔍 Cargando documentos para actividad ID:', actividad.idActividad)
        this.documentosActividad = await DocumentoService.getByActividad(actividad.idActividad)
        console.log('📄 Documentos cargados para edición:', this.documentosActividad.length)
        console.log('📄 Documentos:', this.documentosActividad)
      } catch (error) {
        console.error('❌ Error al cargar documentos de la actividad:', error)
        this.documentosActividad = []
      }

      this.$bvModal.show('modal-actividad')
    },
    async guardarActividad() {
      // Limpiar errores previos
      this.erroresActividad = {
        idTipoActividad: null,
        idEstadoActividad: null,
        fecha: null,
        idProximaAccionTipo: null,
        proximaAccionFecha: null,
      }
      
      // Validaciones requeridas y marcar campos con errores
      let hayErrores = false
      
      if (!this.modalActividad.idTipoActividad) {
        this.erroresActividad.idTipoActividad = false
        hayErrores = true
      }
      
      if (!this.modalActividad.idEstadoActividad) {
        this.erroresActividad.idEstadoActividad = false
        hayErrores = true
      }
      
      if (!this.modalActividad.fecha) {
        this.erroresActividad.fecha = false
        hayErrores = true
      }
      
      if (!this.modalActividad.idProximaAccionTipo) {
        this.erroresActividad.idProximaAccionTipo = false
        hayErrores = true
      }
      
      if (!this.modalActividad.proximaAccionFecha) {
        this.erroresActividad.proximaAccionFecha = false
        hayErrores = true
      }
      
      if (hayErrores) {
        this.$toast?.error('Por favor, complete todos los campos requeridos')
        // Hacer scroll al primer campo con error
        this.$nextTick(() => {
          const primerError = document.querySelector('.is-invalid')
          if (primerError) {
            primerError.scrollIntoView({ behavior: 'smooth', block: 'center' })
            primerError.focus()
          }
        })
        return
      }
      
      this.guardandoActividad = true
      try {
        const actividadData = {
          idTipoActividad: this.modalActividad.idTipoActividad,
          idEstadoActividad: this.modalActividad.idEstadoActividad,
          fecha: this.modalActividad.fecha,
          proximaAccionFecha: this.modalActividad.proximaAccionFecha,
          idProximaAccionTipo: this.modalActividad.idProximaAccionTipo,
          observaciones: this.modalActividad.observaciones,
          recordatorio: this.modalActividad.recordatorio,
          idCreadoPor: this.usuarioActual.idUsuario || 1,
          idProyecto: this.proyecto.idProyecto
        }

        let actividadGuardada
        if (this.modalActividad.esEdicion) {
          // Actualizar actividad existente
          actividadGuardada = await ActividadService.update(this.modalActividad.idActividad, actividadData)
          this.$set(this.proyecto.actividades, this.modalActividad.indice, actividadGuardada)
          
          // Si hay documentos temporales (sin ID), crearlos
          const documentosTemporales = this.documentosActividad.filter(doc => !doc.idDocumento)
          if (documentosTemporales.length > 0) {
            await this.crearDocumentosParaActividad(this.modalActividad.idActividad, documentosTemporales)
          }
        } else {
          // Crear nueva actividad
          actividadGuardada = await ActividadService.create(actividadData)
          
          // Crear documentos si hay documentos temporales
          if (this.documentosActividad && this.documentosActividad.length > 0) {
            await this.crearDocumentosParaActividad(actividadGuardada.idActividad, this.documentosActividad)
          }
          
          this.proyecto.actividades.push(actividadGuardada)
        }

        this.$toast?.success(this.modalActividad.esEdicion ? 'Actividad actualizada correctamente' : 'Actividad creada correctamente')
        
        // Guardar el estado del recordatorio antes de cerrar el modal
        const recordatorioMarcado = this.modalActividad.recordatorio
        
        // Cerrar el modal primero
        this.cerrarModalActividad()
        
        // Si el recordatorio está marcado, mostrar las opciones de recordatorio después de cerrar el modal
        if (recordatorioMarcado) {
          await this.agregarRecordatorioDesdeModal(actividadGuardada)
        }
      } catch (error) {
        console.error('Error al guardar actividad:', error)
        this.$toast?.error('Error al guardar la actividad')
      } finally {
        this.guardandoActividad = false
      }
    },
    async crearDocumentosParaActividad(actividadId, documentos) {
      try {
        for (const documento of documentos) {
          // Verificar si el documento ya tiene un ID (ya fue creado en el backend)
          if (documento.idDocumento) {
            console.log('Documento ya existe, omitiendo:', documento.idDocumento)
            continue
          }
          
          // Mapear los campos del documento temporal a la estructura que espera el backend
          const documentoData = {
            nombreArchivo: documento.nombreArchivo,
            nombreArchivoOriginal: documento.nombreArchivoOriginal || documento.nombreArchivo,
            idCategoriaArchivo: documento.idCategoriaArchivo,
            version: documento.version || 1,
            contenido: documento.contenido,
            idActividad: actividadId,
            idSubidoPor: this.usuarioActual.idUsuario || 1
          }
          
          console.log('📎 Creando documento para actividad:', actividadId)
          console.log('📎 Datos del documento:', documentoData.nombreArchivo)
          
          // Usar DocumentoService para crear el documento
          await DocumentoService.create(documentoData)
          
          console.log('✅ Documento creado exitosamente:', documentoData.nombreArchivo)
        }
        console.log('✅ Documentos creados para actividad:', actividadId)
      } catch (error) {
        console.error('Error al crear documentos:', error)
        throw error
      }
    },
    async eliminarActividad(index) {
      this.$bvModal.msgBoxConfirm('¿Estás seguro de que quieres eliminar esta actividad?', {
        title: 'Confirmar eliminación',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Eliminar',
        cancelTitle: 'Cancelar',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      }).then(async (value) => {
        if (value) {
          try {
            const actividad = this.proyecto.actividades[index]
            await ActividadService.delete(actividad.idActividad)
            this.proyecto.actividades.splice(index, 1)
            this.$toast?.success('Actividad eliminada correctamente')
          } catch (error) {
            console.error('Error al eliminar actividad:', error)
            this.$toast?.error('Error al eliminar la actividad')
          }
        }
      })
    },
    resetearModalActividad() {
      this.modalActividad = {
        esEdicion: false,
        indice: -1,
        idActividad: null,
        idTipoActividad: null,
        idEstadoActividad: null,
        fecha: '',
        proximaAccionFecha: '',
        idProximaAccionTipo: null,
        observaciones: '',
        recordatorio: false,
      }
      // Limpiar errores de validación
      this.erroresActividad = {
        idTipoActividad: null,
        idEstadoActividad: null,
        fecha: null,
        idProximaAccionTipo: null,
        proximaAccionFecha: null,
      }
      // Limpiar documentos
      this.documentosActividad = []
      this.mostrarSubidaDocumentos = false
    },
    cerrarModalActividad() {
      this.$bvModal.hide('modal-actividad')
    },

    // Métodos para gestión de documentos
    onDocumentoSubido(documento) {
      // Si es modo temporal (actividad nueva), agregar a la lista temporal
      if (!this.modalActividad.esEdicion) {
        this.documentosActividad.push({
          nombreArchivo: documento.nombreArchivo,
          nombreArchivoOriginal: documento.nombreArchivoOriginal,
          idCategoriaArchivo: documento.idCategoriaArchivo,
          nombreCategoria: documento.nombreCategoria || documento.nombreCategoriaArchivo,
          version: documento.version,
          contenido: documento.contenido,
          tamaño: documento.tamaño || 0
        })
        this.$toast?.success('Documento agregado correctamente')
      } else {
        // Si es edición, agregar directamente a la lista
        this.documentosActividad.push(documento)
        this.$toast?.success('Documento agregado correctamente')
      }
      this.mostrarSubidaDocumentos = false
    },

    onFinalizarSubida() {
      this.mostrarSubidaDocumentos = false
    },

    async descargarDocumento(id, nombreArchivo) {
      try {
        this.$toast?.info('Descargando documento...')
        
        // Usar el método del servicio que maneja el nombre original automáticamente
        await DocumentoService.downloadDocumento(id)
        
        this.$toast?.success('Documento descargado correctamente')
      } catch (error) {
        console.error('Error al descargar documento:', error)
        this.$toast?.error('Error al descargar el documento')
      }
    },
    eliminarDocumento(index) {
      // Si es modo temporal, solo eliminar de la lista
      if (!this.modalActividad.esEdicion || !this.documentosActividad[index].idDocumento) {
        this.documentosActividad.splice(index, 1)
        this.$toast?.success('Documento eliminado')
        return
      }
      
      // Si es un documento existente, confirmar eliminación
      this.$swal({
        title: '¿Estás seguro?',
        text: 'Este documento será eliminado permanentemente',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const documento = this.documentosActividad[index]
            if (documento.idDocumento) {
              await DocumentoService.delete(documento.idDocumento)
            }
            this.documentosActividad.splice(index, 1)
            this.$toast?.success('Documento eliminado')
          } catch (error) {
            console.error('Error al eliminar documento:', error)
            this.$toast?.error('Error al eliminar el documento')
          }
        }
      })
    },

    async recargarDocumentos() {
      if (!this.modalActividad.idActividad) {
        this.$toast.warning('No hay actividad seleccionada')
        return
      }

      try {
        console.log('🔄 Recargando documentos para actividad:', this.modalActividad.idActividad)
        this.documentosActividad = await DocumentoService.getByActividad(this.modalActividad.idActividad)
        console.log('📄 Documentos recargados:', this.documentosActividad.length)
        this.$toast.success(`Documentos recargados: ${this.documentosActividad.length} encontrados`)
      } catch (error) {
        console.error('❌ Error al recargar documentos:', error)
        this.$toast.error('Error al recargar documentos')
      }
    },

    resetSubidaDocumentos() {
      this.mostrarSubidaDocumentos = false
    },
    async actualizarProyecto() {
      this.guardando = true
      try {
        // Obtener el ID del estado del proyecto
        const idEstadoProyecto = this.estadosProyectos.find(estado => estado.nombre === this.proyecto.estado)?.idEstadoProyecto || this.proyecto.idEstadoProyecto
        
        // Preparar datos para el backend mapeando los campos de los selects
        const proyectoParaBackend = {
          nombre: this.proyecto.nombre,
          descripcion: this.proyecto.descripcion || null,
          fechaInicio: this.proyecto.fechaInicio,
          fechaFin: this.proyecto.fechaFin || null,
          presupuesto: this.proyecto.presupuesto ? parseFloat(this.proyecto.presupuesto) : null,
          idEstadoProyecto: idEstadoProyecto,
          idResponsable: this.proyecto.responsable,
          // Enviar los IDs de las obras para asociarlas al proyecto
          obras: this.proyecto.obras ? this.proyecto.obras.map(obra => obra.idObra).filter(id => id != null) : []
        }
        
        console.log('Enviando proyecto al backend para actualizar:', proyectoParaBackend)
        console.log('Obras a asociar:', proyectoParaBackend.obras)
        
        // Actualizar proyecto en el backend
        await ProyectoService.update(this.proyecto.idProyecto, proyectoParaBackend)
        
        this.$toast?.success('Proyecto actualizado correctamente')
        this.$router.push({ name: 'proyectos-detalle', params: { id: this.proyecto.idProyecto } })
      } catch (error) {
        console.error('Error al actualizar proyecto:', error)
        this.$toast?.error('Error al actualizar el proyecto')
      } finally {
        this.guardando = false
      }
    },
    getEstadoObraVariant(estado) {
      const estados = {
        'En Planificación': 'warning',
        'En Progreso': 'primary',
        'Completada': 'success',
        'Cancelada': 'danger',
        'Pausada': 'secondary'
      }
      return estados[estado] || 'secondary'
    },
    getEstadoActividadVariant(estado) {
      const estados = {
        'Pendiente': 'warning',
        'En Progreso': 'primary',
        'Completada': 'success',
        'Cancelada': 'danger',
        'Contactado': 'info',
        'En Espera': 'secondary'
      }
      return estados[estado] || 'secondary'
    },
    volver() {
      this.$router.push({ name: 'proyectos-lista' })
    },
    
    // Métodos para manejar empresas
    mostrarModalEmpresa() {
      this.resetModalEmpresa()
      this.$bvModal.show('modal-empresa')
    },
    
    resetModalEmpresa() {
      this.modalEmpresa = {
        nombre: '',
        direccion: '',
        idTipoEmpresa: '',
        cif: '',
        web: '',
        telefono: '',
        email: '',
        observaciones: '',
      }
    },
    
    async guardarEmpresa() {
      // Validaciones requeridas
      if (!this.modalEmpresa.nombre.trim()) {
        this.$toast?.warning('El nombre de la empresa es requerido')
        return
      }
      
      if (!this.modalEmpresa.cif.trim()) {
        this.$toast?.warning('El CIF es requerido')
        return
      }
      
      if (!this.modalEmpresa.direccion.trim()) {
        this.$toast?.warning('La dirección es requerida')
        return
      }
      
      if (!this.modalEmpresa.idTipoEmpresa) {
        this.$toast?.warning('El tipo de empresa es requerido')
        return
      }
      
      this.guardandoEmpresa = true
      try {
        // Crear la empresa
        const empresaCreada = await EmpresaService.create({
          nombre: this.modalEmpresa.nombre,
          direccion: this.modalEmpresa.direccion,
          idTipoEmpresa: this.modalEmpresa.idTipoEmpresa,
          cif: this.modalEmpresa.cif,
          web: this.modalEmpresa.web || null,
          telefono: this.modalEmpresa.telefono || null,
          email: this.modalEmpresa.email || null,
          observaciones: this.modalEmpresa.observaciones || null,
        })
        
        // Actualizar la lista de empresas
        const empresas = await EmpresaService.getAll()
        this.opcionesEmpresas = [
          { value: '', text: 'Seleccionar empresa' },
          ...empresas.map(empresa => ({
            value: empresa.idEmpresa,
            text: empresa.nombre
          }))
        ]
        
        // Seleccionar automáticamente la nueva empresa en el modal de obra
        this.modalObra.idEmpresa = empresaCreada.idEmpresa
        
        this.$toast?.success('Empresa creada correctamente')
        
        this.$bvModal.hide('modal-empresa')
      } catch (error) {
        console.error('Error al crear empresa:', error)
        this.$toast?.error('Error al crear la empresa')
      } finally {
        this.guardandoEmpresa = false
      }
    },
    async agregarRecordatorioDesdeModal(actividad = null) {
      // Si se pasa una actividad guardada, usarla; si no, construirla desde el modal
      const actividadAUsar = actividad ? this.construirActividadDesdeGuardada(actividad) : this.construirActividadDesdeModal()
      
      // Validar que haya fecha de próxima acción
      if (!actividadAUsar.proximaAccionFecha) {
        this.$swal({
          title: 'Fecha requerida',
          text: 'Debe seleccionar una fecha de próxima acción para configurar el recordatorio',
          icon: 'warning',
          confirmButtonText: 'Aceptar',
          customClass: {
            confirmButton: 'btn btn-primary',
          },
          buttonsStyling: false,
        })
        return
      }
      
      // Usar solo la fecha de próxima acción
      const fechaRecordatorio = actividadAUsar.proximaAccionFecha
      const result = await this.$swal({
        title: 'Configurar Recordatorio',
        html: `
          <div class="text-left">
            <p>Selecciona la hora para el recordatorio:</p>
            <div class="form-group">
              <label for="hora">Hora:</label>
              <input type="time" id="hora" class="form-control" value="09:00" required>
            </div>
            <p class="text-muted small mt-2">Fecha: ${this.formatearFecha(fechaRecordatorio)}</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
        didOpen: () => {
          // Asegurar que el input esté enfocado
          setTimeout(() => {
            const input = document.getElementById('hora')
            if (input) input.focus()
          }, 100)
        },
        preConfirm: () => {
          const horaInput = document.getElementById('hora')
          if (!horaInput || !horaInput.value) {
            this.$swal.showValidationMessage('Por favor selecciona una hora')
            return false
          }
          return horaInput.value
        }
      })

      if (!result.value) {
        // Si se cancela, simplemente retornar
        return
      }
      
      const horaRecordatorio = result.value

      // Crear el evento con la hora seleccionada
      const evento = this.crearEventoDesdeActividadCompleto(actividadAUsar, horaRecordatorio)
      
      // Guardar referencia para usar en el segundo diálogo
      const self = this
      
      this.$swal({
        title: 'Agregar Recordatorio',
        text: 'Selecciona cómo quieres agregar el recordatorio:',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Google Calendar',
        cancelButtonText: 'Cancelar',
        showDenyButton: true,
        denyButtonText: 'Outlook',
        showCloseButton: true,
        customClass: {
          confirmButton: 'btn btn-success',
          denyButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          // Google Calendar
          const url = generarEnlaceGoogleCalendar(evento)
          window.open(url, '_blank')
        } else if (result.isDenied) {
          // Outlook
          const url = generarEnlaceOutlookCalendar(evento)
          window.open(url, '_blank')
        }
      })
      
      // Mostrar opciones adicionales
      this.$swal({
        title: 'Otras opciones',
        text: 'También puedes descargar el archivo o enviarlo por correo:',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Descargar archivo',
        cancelButtonText: 'Cerrar',
        showDenyButton: true,
        denyButtonText: 'Enviar por correo',
        customClass: {
          confirmButton: 'btn btn-info',
          denyButton: 'btn btn-warning',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          descargarArchivoICS(evento)
        } else if (result.isDenied) {
          await self.enviarRecordatorioPorCorreoDesdeModal(actividadAUsar, horaRecordatorio)
        }
      })
    },
    construirActividadDesdeModal() {
      // Obtener los nombres de los campos
      const tipoActividadNombre = this.opcionesTiposActividad.find(
        opcion => opcion.value === this.modalActividad.idTipoActividad
      )?.text || 'Actividad'

      const estadoActividadNombre = this.opcionesEstadosActividad.find(
        opcion => opcion.value === this.modalActividad.idEstadoActividad
      )?.text || ''

      const proximaAccionNombre = this.opcionesAccionesActividad.find(
        opcion => opcion.value === this.modalActividad.idProximaAccionTipo
      )?.text || ''

      return {
        idActividad: this.modalActividad.idActividad,
        tipoActividad: {
          idTipoActividad: this.modalActividad.idTipoActividad,
          nombre: tipoActividadNombre
        },
        estadoActividad: {
          idEstadoActividad: this.modalActividad.idEstadoActividad,
          nombre: estadoActividadNombre
        },
        proximaAccionTipo: {
          idAccionActividad: this.modalActividad.idProximaAccionTipo,
          nombre: proximaAccionNombre
        },
        proximaAccionFecha: this.modalActividad.proximaAccionFecha,
        observaciones: this.modalActividad.observaciones,
        nombreProyecto: this.proyecto.nombre || ''
      }
    },
    construirActividadDesdeGuardada(actividad) {
      // Construir un objeto actividad desde la actividad guardada
      // Manejar tanto el caso donde viene con objetos anidados como con nombres planos
      const tipoActividadNombre = actividad.tipoActividad?.nombre || 
                                  actividad.nombreTipoActividad || 
                                  this.opcionesTiposActividad.find(
                                    opcion => opcion.value === (actividad.tipoActividad?.idTipoActividad || actividad.idTipoActividad || this.modalActividad.idTipoActividad)
                                  )?.text || 'Actividad'

      const estadoActividadNombre = actividad.estadoActividad?.nombre || 
                                    actividad.nombreEstadoActividad || 
                                    this.opcionesEstadosActividad.find(
                                      opcion => opcion.value === (actividad.estadoActividad?.idEstadoActividad || actividad.idEstadoActividad || this.modalActividad.idEstadoActividad)
                                    )?.text || ''

      const proximaAccionNombre = actividad.proximaAccionTipo?.nombre || 
                                  actividad.nombreProximaAccionTipo || 
                                  this.opcionesAccionesActividad.find(
                                    opcion => opcion.value === (actividad.proximaAccionTipo?.idAccionActividad || actividad.idProximaAccionTipo || this.modalActividad.idProximaAccionTipo)
                                  )?.text || ''
      
      return {
        idActividad: actividad.idActividad,
        tipoActividad: {
          idTipoActividad: actividad.tipoActividad?.idTipoActividad || actividad.idTipoActividad || this.modalActividad.idTipoActividad,
          nombre: tipoActividadNombre
        },
        estadoActividad: {
          idEstadoActividad: actividad.estadoActividad?.idEstadoActividad || actividad.idEstadoActividad || this.modalActividad.idEstadoActividad,
          nombre: estadoActividadNombre
        },
        proximaAccionTipo: {
          idAccionActividad: actividad.proximaAccionTipo?.idAccionActividad || actividad.idProximaAccionTipo || this.modalActividad.idProximaAccionTipo,
          nombre: proximaAccionNombre
        },
        proximaAccionFecha: actividad.proximaAccionFecha || this.modalActividad.proximaAccionFecha,
        observaciones: actividad.observaciones || this.modalActividad.observaciones,
        nombreProyecto: this.proyecto.nombre || actividad.nombreProyecto || ''
      }
    },
    async enviarRecordatorioPorCorreoDesdeModal(actividad, horaRecordatorio) {
      const { value: emails } = await this.$swal({
        title: 'Enviar Recordatorio por Correo',
        html: `
          <div class="text-left">
            <p>Ingresa las direcciones de correo electrónico:</p>
            <div class="form-group">
              <label for="emails">Correos electrónicos:</label>
              <textarea 
                id="emails" 
                class="form-control" 
                rows="3" 
                placeholder="ejemplo1@correo.com, ejemplo2@correo.com, ejemplo3@correo.com"
                required
              ></textarea>
              <small class="form-text text-muted">Separa múltiples correos con comas</small>
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Enviar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-warning',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
        preConfirm: () => {
          const emailsInput = document.getElementById('emails')
          if (!emailsInput || !emailsInput.value.trim()) {
            this.$swal.showValidationMessage('Debes ingresar al menos una dirección de correo')
            return false
          }
          
          const emails = emailsInput.value.split(',').map(email => email.trim()).filter(email => email)
          if (emails.length === 0) {
            this.$swal.showValidationMessage('Debes ingresar al menos una dirección de correo válida')
            return false
          }
          
          // Validar cada email
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          const emailsInvalidos = emails.filter(email => !emailRegex.test(email))
          if (emailsInvalidos.length > 0) {
            this.$swal.showValidationMessage(`Correos inválidos: ${emailsInvalidos.join(', ')}`)
            return false
          }
          
          return emails
        }
      })

      if (emails && emails.length > 0) {
        try {
          this.$swal({
            title: 'Enviando...',
            text: `Enviando recordatorio a ${emails.length} destinatario${emails.length > 1 ? 's' : ''}...`,
            icon: 'info',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
              this.$swal.showLoading()
            }
          })

          // Generar el evento y el archivo ICS con la hora seleccionada
          const evento = this.crearEventoDesdeActividadCompleto(actividad, horaRecordatorio)
          const archivoICS = this.generarArchivoICS(evento)
          
          // Enviar el archivo ICS por correo a múltiples destinatarios
          await this.enviarCorreoRecordatorio(actividad.idActividad || null, emails, archivoICS, evento)
          
          this.$swal({
            title: '¡Enviado!',
            text: `El recordatorio ha sido enviado a ${emails.length} destinatario${emails.length > 1 ? 's' : ''}`,
            icon: 'success',
            confirmButtonText: 'Aceptar',
            customClass: {
              confirmButton: 'btn btn-success',
            },
            buttonsStyling: false,
          })
        } catch (error) {
          console.error('Error al enviar el correo:', error)
          this.$swal({
            title: 'Error',
            text: 'No se pudo enviar el recordatorio. Inténtalo de nuevo.',
            icon: 'error',
            confirmButtonText: 'Aceptar',
            customClass: {
              confirmButton: 'btn btn-danger',
            },
            buttonsStyling: false,
          })
        }
      }
    },
    crearEventoDesdeActividadCompleto(actividad, hora = '09:00') {
      // Usar solo la fecha de próxima acción para el recordatorio
      if (!actividad.proximaAccionFecha) {
        throw new Error('La fecha de próxima acción es requerida')
      }
      
      const fechaBase = new Date(actividad.proximaAccionFecha)
      
      // Combinar la fecha con la hora seleccionada
      const [horas, minutos] = hora.split(':').map(Number)
      const fechaEvento = new Date(fechaBase)
      fechaEvento.setHours(horas, minutos, 0, 0)
      
      // Crear fecha de fin (1 hora después)
      const fechaFin = new Date(fechaEvento)
      fechaFin.setHours(fechaEvento.getHours() + 1)
      
      // Construir el título con el tipo de actividad
      const titulo = actividad.tipoActividad?.nombre || 'Actividad'
      
      // Construir la descripción completa con toda la información
      let descripcion = ''
      
      if (actividad.estadoActividad?.nombre) {
        descripcion += `Estado: ${actividad.estadoActividad.nombre}\n`
      }
      
      if (actividad.proximaAccionTipo?.nombre) {
        descripcion += `Próxima acción: ${actividad.proximaAccionTipo.nombre}\n`
      }
      
      if (actividad.nombreProyecto) {
        descripcion += `Proyecto: ${actividad.nombreProyecto}\n`
      }
      
      if (actividad.observaciones) {
        descripcion += `\nObservaciones:\n${actividad.observaciones}`
      }
      
      if (!descripcion) {
        descripcion = 'Actividad del proyecto'
      }
      
      return {
        titulo: titulo,
        descripcion: descripcion.trim(),
        fechaInicio: fechaEvento,
        fechaFin: fechaFin,
        ubicacion: actividad.nombreProyecto || ''
      }
    },
    generarArchivoICS(evento) {
      const formatearFechaParaICS = (fecha) => {
        return fecha.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
      }
      
      const fechaInicio = formatearFechaParaICS(evento.fechaInicio)
      const fechaFin = formatearFechaParaICS(evento.fechaFin || evento.fechaInicio)
      const fechaActual = formatearFechaParaICS(new Date())
      
      return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Gescom//Actividad//ES
BEGIN:VEVENT
UID:${Date.now()}@gescom.com
DTSTAMP:${fechaActual}
DTSTART:${fechaInicio}
DTEND:${fechaFin}
SUMMARY:${evento.titulo || 'Actividad'}
DESCRIPTION:${evento.descripcion || ''}
LOCATION:${evento.ubicacion || ''}
END:VEVENT
END:VCALENDAR`
    },
    async enviarCorreoRecordatorio(actividadId, emails, archivoICS, evento) {
      try {
        const response = await this.$http.post('/email/recordatorio-actividad', {
          actividadId: actividadId,
          emailsDestino: emails,
          archivoICS: archivoICS,
          evento: evento
        })
        
        if (response.data.success) {
          return Promise.resolve()
        } else {
          throw new Error(response.data.message || 'Error al enviar el correo')
        }
      } catch (error) {
        console.error('Error al enviar correo:', error)
        throw new Error('Error al enviar el recordatorio por correo')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.proyectos-editar {
  padding: 1.5rem;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.content-header-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.gap-2 {
  gap: 0.5rem;
}

.obra-item, .actividad-item {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  background-color: #f8f9fa;
  transition: all 0.2s ease;
}

.obra-item:hover, .actividad-item:hover {
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
}

.obra-item h6, .actividad-item h6 {
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.obra-item .text-muted, .actividad-item .text-muted {
  font-size: 0.9rem;
  line-height: 1.4;
}

.gap-2 {
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .content-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .content-header-right {
    margin-top: 1rem;
  }
  
  .obra-item, .actividad-item {
    padding: 0.75rem;
  }
  
  .d-flex.gap-2 {
    flex-direction: column;
    gap: 0.25rem;
  }
}

// Estilos para las tarjetas de obras y actividades
.obra-card, .actividad-card {
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #007bff;
  }
  
  .card-title {
    font-weight: 600;
    color: #2c3e50;
  }
  
  .badge {
    font-size: 0.75rem;
    font-weight: 500;
  }
  
  small {
    font-size: 0.8rem;
    line-height: 1.4;
  }
}

.obra-card {
  border-left: 4px solid #007bff;
  
  &:hover {
    border-left-color: #0056b3;
  }
}

.actividad-card {
  border-left: 4px solid #28a745;
  
  &:hover {
    border-left-color: #1e7e34;
  }
}

// Estilos para la lista de obras seleccionables
.obras-list {
  max-height: 400px;
  overflow-y: auto;
  
  .obra-item {
    padding: 1rem;
    border: 1px solid #e9ecef;
    border-radius: 0.375rem;
    margin-bottom: 0.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background-color: #f8f9fa;
      border-color: #007bff;
    }
    
    &.selected {
      background-color: #e3f2fd;
      border-color: #2196f3;
      border-left: 4px solid #2196f3;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
}

/* Estilos para documentos */
.documento-item {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.documento-item:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.documento-info {
  flex: 1;
}

.documento-nombre {
  font-weight: 500;
  color: #495057;
  display: flex;
  align-items: center;
}
</style>
